import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { LookupRecord } from '../../api/masterData';

export type LookupFieldType = 'text' | 'number' | 'boolean' | 'color' | 'textarea' | 'select';

export interface LookupFieldConfig {
  name: string;
  label: string;
  type: LookupFieldType;
  required?: boolean;
  /** 'select' only — static options (e.g. an already-fetched Currency list). */
  options?: Array<{ value: number | string; label: string }>;
}

interface LookupFormDialogProps {
  open: boolean;
  title: string;
  fields: LookupFieldConfig[];
  editingRecord: LookupRecord | null;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
}

const defaultValueFor = (type: LookupFieldType) => (type === 'boolean' ? false : '');

export const LookupFormDialog = ({
  open,
  title,
  fields,
  editingRecord,
  errorMessage,
  onClose,
  onSubmit,
}: LookupFormDialogProps) => {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const initial: Record<string, unknown> = {};
      for (const field of fields) {
        initial[field.name] = editingRecord
          ? (editingRecord[field.name] ?? defaultValueFor(field.type))
          : defaultValueFor(field.type);
      }
      setValues(initial);
    }
  }, [open, editingRecord, fields]);

  const setField = (name: string, value: unknown) => setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = fields.every((f) => !f.required || `${values[f.name] ?? ''}`.trim() !== '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {fields.map((field) => {
            if (field.type === 'boolean') {
              return (
                <FormControlLabel
                  key={field.name}
                  control={
                    <Checkbox
                      checked={!!values[field.name]}
                      onChange={(e) => setField(field.name, e.target.checked)}
                    />
                  }
                  label={field.label}
                />
              );
            }
            if (field.type === 'select') {
              return (
                <TextField
                  key={field.name}
                  select
                  label={field.label}
                  required={field.required}
                  fullWidth
                  value={values[field.name] ?? ''}
                  onChange={(e) => setField(field.name, e.target.value ? Number(e.target.value) : '')}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {field.options?.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }
            return (
              <TextField
                key={field.name}
                label={field.label}
                type={field.type === 'number' ? 'number' : field.type === 'color' ? 'text' : 'text'}
                multiline={field.type === 'textarea'}
                minRows={field.type === 'textarea' ? 4 : undefined}
                required={field.required}
                fullWidth
                value={values[field.name] ?? ''}
                onChange={(e) => setField(field.name, e.target.value)}
                InputProps={
                  field.type === 'color'
                    ? {
                        startAdornment: (
                          <span
                            style={{
                              display: 'inline-block',
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              marginRight: 8,
                              backgroundColor: String(values[field.name] ?? '#9e9e9e'),
                              border: '1px solid rgba(0,0,0,0.2)',
                            }}
                          />
                        ),
                      }
                    : undefined
                }
              />
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!isValid || submitting} onClick={handleSubmit}>
          {submitting ? 'Saving…' : editingRecord ? 'Save changes' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
